import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService, FeedbackStandEndpointsService, FeedbackStand, GlobalVariables, MoodEnum } from 'app/common';
import { TranslationKeys } from 'app/common/translations';
import { heightTransition, fadePanelTransition } from 'app/common/common.animation';

declare var swal: any;

@Component({
  selector: 'feedback-stand',
  templateUrl: './feedback-stand.component.html',
  styleUrls: ['./feedback-stand.component.scss'],
  providers: [FeedbackStandEndpointsService],
  animations: [heightTransition, fadePanelTransition],
})
export class FeedbackStandComponent implements OnInit {
  feedbackStandData: FeedbackStand = new FeedbackStand();
  message = '';
  moodSelected = false;
  currentMood: any = null;
  moods: any[] = [];
  questions: any[] = [];
  routeId: string = '';
  readonly moodColors: Record<number, string> = {
    [MoodEnum.excellent]: 'rgba(13, 179, 27, 0.75)',
    [MoodEnum.good]: 'rgb(181, 209, 87)',
    [MoodEnum.indifferent]: 'rgba(245, 149, 4, 0.92)',
    [MoodEnum.bad]: 'rgba(226, 37, 37, 0.75)',
  };

  private readonly moodTranslationKeys: Record<number, keyof TranslationKeys> = {
    [MoodEnum.excellent]: 'feedback_excellent',
    [MoodEnum.good]: 'feedback_good',
    [MoodEnum.indifferent]: 'feedback_indifferent',
    [MoodEnum.bad]: 'feedback_bad',
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dataService: DataService,
    private feedbackStandEndpointsService: FeedbackStandEndpointsService,
  ) {}

  ngOnInit() {
    this.routeParams();
    this.loadFeedbackStand();
  }

  routeParams() {
    this.route.params.subscribe((params) => {
      this.routeId = params['id'];
    });
  }

  loadFeedbackStand() {
    this.feedbackStandEndpointsService.getFeedbackStandData(this.routeId).subscribe({
      next: (data: any) => {
        this.feedbackStandData = new FeedbackStand();
        this.feedbackStandData.setFeedbackStandData(data.data);
        this.moods = this.feedbackStandData.feedbackMoods || [];
        this.restoreMoodSelection();
      },
      error: (error) => {
        if (error[0].status == '404') {
          this.message = GlobalVariables.ERROR_MESSAGE_404;
        } else {
          this.message = GlobalVariables.DEFAULT_ERROR_MESSAGE;
        }

        this.alertMessage(this.message, error);
      },
    });
  }

  alertMessage(message: string, error: any = null) {
    swal({
      text: message,
      type: 'warning',
      allowOutsideClick: false,
      showCancelButton: false,
      allowEscapeKey: false,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Refresh the page!',
    }).then(() => {
      if (error?.[0]?.status == '401') {
        this.dataService.clearData();
        this.router.navigate(['login']);
      } else {
        location.reload();
      }
    });
  }

  setDynamicClass(moodType: number) {
    if (moodType == MoodEnum['excellent']) {
      return 'row-excellent';
    } else if (moodType == MoodEnum['good']) {
      return 'row-good';
    } else if (moodType == MoodEnum['indifferent']) {
      return 'row-indifferent';
    } else if (moodType == MoodEnum['bad']) {
      return 'row-bad';
    }

    return '';
  }

  moodLogo(moodType: number) {
    if (moodType == MoodEnum['excellent']) {
      return '/assets/images/excellent-smiley.svg';
    } else if (moodType == MoodEnum['good']) {
      return '/assets/images/good-smiley.svg';
    } else if (moodType == MoodEnum['indifferent']) {
      return '/assets/images/neutral-smiley.svg';
    } else if (moodType == MoodEnum['bad']) {
      return '/assets/images/bad-smiley.svg';
    }

    return '';
  }

  setFeedbackQuestions(moodType: number) {
    const selectedMood = this.feedbackStandData.feedbackMoods[moodType];
    if (!selectedMood) {
      return;
    }

    this.currentMood = selectedMood;
    this.questions = selectedMood.questions || [];
    this.moodSelected = true;
    this.persistMoodSelection(moodType);
  }

  getMoodTranslationKey(moodType: number): keyof TranslationKeys {
    return this.moodTranslationKeys[moodType] ?? 'feedback_bad';
  }

  getMoodColor(moodType: number): string {
    return this.moodColors[moodType] || '#808080';
  }

  refreshPage() {
    location.reload();
  }

  onQuestionnaireCompleted() {
    this.resetToMoodSelection();
  }

  private persistMoodSelection(moodType: number) {
    window.localStorage.setItem(this.getMoodStorageKey(), String(moodType));
  }

  private restoreMoodSelection() {
    const storedMoodType = window.localStorage.getItem(this.getMoodStorageKey());
    if (storedMoodType === undefined || storedMoodType === null) {
      return;
    }

    const moodType = +storedMoodType;
    if (isNaN(moodType) || !this.feedbackStandData.feedbackMoods[moodType]) {
      this.clearMoodSelection();
      return;
    }

    this.setFeedbackQuestions(moodType);
  }

  private clearMoodSelection() {
    window.localStorage.removeItem(this.getMoodStorageKey());
  }

  private resetToMoodSelection() {
    this.moodSelected = false;
    this.currentMood = null;
    this.questions = [];
    this.clearMoodSelection();
  }

  private getMoodStorageKey() {
    return `feedbackStand.selectedMood.${this.routeId}`;
  }
}
